/**
 * EINE ANSCHRIFT EINGEBEN STATT ZWEIER ZAHLEN - AN BEIDEN KARTEN.
 *
 * ORIGINALTEXT MARVIN (10.09.2026)
 * > „ich muss beim „Auftrag vorbereiten" und bei der „planung vorbereiten" die
 * >  möglichkeit haben eine adresse einzugeben, wichtig in dem fall ist, dass
 * >  ich nur straßen und hausnummer angeben kann, PLZ und Ort sind ja durch den
 * >  auftrag schon festgelegt. Mache keine pflichtfelder daraus. ich muss eine
 * >  andere adresse finden können, wenn es nicht direkt angezeigt wird. die
 * >  eingabe von koordinaten kannst du bei beiden karten komplett entfernen.
 * >  wir ersetzen die eingabe damit."
 *
 * **DIE KOORDINATENEINGABE HAT DEN INNENDIENST AUS DEM SYSTEM GESCHICKT.** Die
 * beiden Zahlen bekam er auf einer fremden Karte, mit der rechten Maustaste,
 * und die Breite steht ZUERST; der Satz daneben musste das erklären, und wer
 * sie vertauscht, landet im Indischen Ozean. **Eine Anschrift ist das, was der
 * Mensch wirklich weiss** - findet der Dienst die 37 nicht, kennt er die 35,
 * und das sind zwanzig Meter daneben statt dreihundert.
 *
 * ES STEHT HIER UND NICHT ZWEIMAL IN DEN BEIDEN SEITEN. Der Lageplan und die
 * Planung zeigen dieselben zwei Felder und dieselbe feste Zeile daneben; zwei
 * Fassungen liefen beim ersten Nachtrag auseinander, und dann bietet der eine
 * Bildschirm etwas an, das der andere nicht kennt. Dieselbe Aufteilung wie bei
 * `KeinePunkte.tsx` und `Punktgenauigkeit.tsx`.
 *
 * **UND DIE FACHREGEL STEHT IM SERVER** (`regeln/adresssuche.ts`): was
 * gesucht wird, welche Genauigkeit dabei herauskommt und welcher Satz danach
 * dasteht. Hier stehen zwei Felder und ein Knopf.
 */
import { useState } from 'react';
import { api } from '../api.ts';

export interface Anschrift {
  strasse: string;
  hausnummer: string;
  plz: string;
  ort: string;
}

export default function Adresssuche(
  { auftragId, anschrift, aufschrift, fertig }: {
    auftragId: string;
    anschrift?: Anschrift | null;
    /** Die Aufschrift des Knopfes. Der Vorgang ist derselbe, der Anlass nicht. */
    aufschrift?: string;
    fertig?: () => void;
  }
) {
  /* VORBELEGT MIT DER ANSCHRIFT DES AUFTRAGS. Wer eine Nachbarnummer probieren
     will, ändert EINE Ziffer; wer die Anschrift des Auftrags sucht, drückt nur
     den Knopf. Ein leeres Feldpaar hiesse, dieselbe Straße jedes Mal neu zu
     tippen - und beim dritten Mal tippt jemand daneben. */
  const [strasse, setStrasse] = useState(anschrift?.strasse ?? '');
  const [hausnummer, setHausnummer] = useState(anschrift?.hausnummer ?? '');
  /* DER ORTSTEIL (.451): in 53505 gibt es drei Dorfstraßen. */
  const [ortsteil, setOrtsteil] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehlgeschlagen, setFehlgeschlagen] = useState(false);
  /* DER UMKREIS (.245). Marvin: „kannst du automatisch im umkreis suchen was
     es für andere straßen gibt und uns dann einen vorschlag mit entfernung
     zur Auftragsadresse ausgeben?" */
  const [umkreis, setUmkreis] = useState<any | null>(null);
  const [umkreisLaeuft, setUmkreisLaeuft] = useState(false);

  async function imUmkreis() {
    setUmkreisLaeuft(true);
    try { setUmkreis(await api.umkreis(auftragId)); }
    catch (f: any) { setUmkreis({ strassen: [], meldung: f?.fehler ?? 'Die Umkreissuche hat nicht geklappt.' }); }
    finally { setUmkreisLaeuft(false); }
  }

  async function punktUebernehmen() {
    setLaeuft(true); setMeldung(null);
    try {
      const r = await api.koordinatenSetzen(auftragId, { vorschlag: 'bezug' });
      setMeldung(r.meldung ?? 'Punkt übernommen.'); setFehlgeschlagen(false); setUmkreis(null);
      fertig?.();
    } catch (f: any) {
      setMeldung([f?.fehler, f?.hinweis].filter(Boolean).join(' ') || 'Das hat nicht geklappt.');
    } finally { setLaeuft(false); }
  }

  async function suchen() {
    setLaeuft(true); setMeldung(null);
    try {
      const r = await api.koordinatenSetzen(auftragId, { strasse, hausnummer, ortsteil: ortsteil.trim() || undefined });
      setMeldung(r.meldung ?? 'Punkt eingetragen.');
      setFehlgeschlagen(false); setUmkreis(null);
      fertig?.();
    } catch (f: any) {
      setMeldung([f?.fehler, f?.hinweis].filter(Boolean).join(' ')
                 || 'Das hat nicht geklappt.');
      setFehlgeschlagen(true);
    } finally { setLaeuft(false); }
  }

  const festeZeile = [anschrift?.plz, anschrift?.ort]
    .filter((w) => (w ?? '').trim().length > 0).join(' ');

  return (
    <div style={{ marginTop: 10 }}>
      <div className="reihe">
        <span className="feldpaar">
          <label className="klein" htmlFor={'suche_strasse_' + auftragId}>
            Straße
          </label>
          <input id={'suche_strasse_' + auftragId} value={strasse}
                 style={{ width: 200 }} autoComplete="off"
                 disabled={laeuft}
                 onChange={(e) => setStrasse(e.target.value)} />
        </span>
        <span className="feldpaar">
          <label className="klein" htmlFor={'suche_hausnummer_' + auftragId}>
            Hausnummer
          </label>
          <input id={'suche_hausnummer_' + auftragId} value={hausnummer}
                 style={{ width: 100 }} autoComplete="off"
                 disabled={laeuft}
                 onChange={(e) => setHausnummer(e.target.value)} />
        </span>
        <span className="feldpaar">
          <label className="klein" htmlFor={'suche_ortsteil_' + auftragId}>
            Ortsteil (wenn die Straße mehrmals vorkommt)
          </label>
          <input id={'suche_ortsteil_' + auftragId} value={ortsteil}
                 style={{ width: 160 }} autoComplete="off" placeholder="z. B. Altenburg"
                 disabled={laeuft}
                 onChange={(e) => setOrtsteil(e.target.value)} />
        </span>
        {/* PLZ UND ORT STEHEN ALS TEXT DA UND IN KEINEM FELD. Sie sind durch
            den Auftrag festgelegt, und eine Angabe, die man nicht ändern kann,
            gehört in kein Eingabefeld: dort probiert sie beim zwanzigsten Mal
            jemand aus. Der Server liest sie ohnehin nicht aus dem Rumpf. */}
        {festeZeile && (
          <span className="klein leise" style={{ alignSelf: 'end' }}>
            {festeZeile}
          </span>
        )}
        {/* KEIN PFLICHTFELD, ALSO KEIN GESPERRTER KNOPF. Wer nichts eintippt,
            sucht die Anschrift des Auftrags - genau das, was der Knopf
            „Koordinaten suchen" vorher allein getan hat. */}
        <button className="knopf haupt" disabled={laeuft} onClick={suchen}>
          {laeuft ? 'Wird gesucht' : (aufschrift ?? 'Punkt suchen')}
        </button>
      </div>
      <p className="klein leise" style={{ marginBottom: 0, marginTop: 6 }}>
        Findet der Dienst die Hausnummer nicht, tragen Sie eine Nachbarnummer
        derselben Straße ein. PLZ und Ort stehen fest, sie kommen aus dem
        Auftrag.
      </p>
      {meldung && <div className="klein" style={{ marginTop: 8 }}>{meldung}</div>}
      {fehlgeschlagen && !umkreis && (
        <button className="knopf klein" style={{ marginTop: 8 }} disabled={umkreisLaeuft}
                onClick={imUmkreis}>
          {umkreisLaeuft ? 'Sucht im Umkreis …' : 'Im Umkreis suchen'}
        </button>
      )}
      {umkreis && (
        <div className="umkreis" style={{ marginTop: 10 }}>
          <div className="klein">{umkreis.meldung}</div>
          {umkreis.bezug && umkreis.bezug.art !== 'ort' && (
            <div className="reihe" style={{ gap: 8, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span className="klein"><b>Gefunden:</b> {umkreis.bezug.text}</span>
              <button className="knopf klein haupt" disabled={laeuft} onClick={punktUebernehmen}>
                Diesen Punkt übernehmen
              </button>
            </div>
          )}
          {(umkreis.strassen ?? []).length > 0 && (
            <table className="liste" style={{ marginTop: 8 }}>
              <thead>
                <tr><th>Straße im Umkreis</th>
                    <th className="zahl">{umkreis.bezug?.art === 'ort' ? 'zur Ortsmitte' : 'zur Auftragsadresse'}</th>
                    <th></th></tr>
              </thead>
              <tbody>
                {umkreis.strassen.map((x: any) => (
                  <tr key={x.name} style={{ cursor: 'default' }}>
                    <td>{x.name}</td>
                    <td className="zahl">{x.meter < 1000 ? `${x.meter} m` : `${(x.meter / 1000).toFixed(1).replace('.', ',')} km`}</td>
                    <td><button className="knopf klein" disabled={laeuft}
                                onClick={() => { setStrasse(x.name); setHausnummer(''); }}>
                      übernehmen
                    </button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {(umkreis.strassen ?? []).length > 0 && (
            <p className="klein leise" style={{ margin: '6px 0 0' }}>
              „übernehmen" setzt den Straßennamen oben ein. Hausnummer eintragen, dann „Punkt suchen".
            </p>
          )}
        </div>
      )}
    </div>
  );
}
